import { getSupportedRecordingMimeType } from './camera-device.js';
import {
  enqueueRecordingChunk,
  saveRecordingSession,
  updateRecordingSession
} from './recording-queue.js';
import { syncPendingRecordings } from './recording-sync.js';

export function createResilientRecorder(
  stream,
  {
    sessionId = crypto.randomUUID(),
    segmentDuration = 5000,
    preferredMimeType = '',
    videoBitsPerSecond = null,
    audioBitsPerSecond = null,
    capture = null,
    output = null,
    ffmpeg = null,
    onChunkQueued,
    onSyncProgress,
    onStateChange
  } = {}
) {
  const mimeType = getSupportedRecordingMimeType(preferredMimeType);
  let currentRecorder = null;
  let segmentTimer = null;
  let segmentFinished = Promise.resolve();
  let chunkIndex = 0;
  let running = false;

  async function queueSegment(parts) {
    const blob = new Blob(parts, { type: mimeType || parts[0]?.type || 'video/webm' });

    if (blob.size === 0) {
      return;
    }

    const index = chunkIndex++;
    await enqueueRecordingChunk({
      sessionId,
      index,
      blob,
      mimeType: blob.type
    });

    onChunkQueued?.({ sessionId, index, size: blob.size });
    syncPendingRecordings({ onProgress: onSyncProgress }).catch(() => {});
  }

  function startSegment() {
    if (!running) {
      return;
    }

    const parts = [];
    const recorderOptions = {
      ...(mimeType ? { mimeType } : {}),
      ...(videoBitsPerSecond ? { videoBitsPerSecond } : {}),
      ...(audioBitsPerSecond ? { audioBitsPerSecond } : {})
    };
    const recorder = new MediaRecorder(stream, recorderOptions);
    currentRecorder = recorder;

    segmentFinished = new Promise((resolve, reject) => {
      recorder.ondataavailable = ({ data }) => {
        if (data?.size > 0) {
          parts.push(data);
        }
      };

      recorder.onerror = (event) => {
        reject(event.error || new Error('MediaRecorder error'));
      };

      recorder.onstop = async () => {
        clearTimeout(segmentTimer);

        try {
          await queueSegment(parts);
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          currentRecorder = null;

          if (running) {
            startSegment();
          }
        }
      };
    });

    recorder.start();
    segmentTimer = setTimeout(() => {
      if (recorder.state === 'recording') {
        recorder.stop();
      }
    }, segmentDuration);
  }

  return {
    sessionId,
    mimeType,

    async start() {
      if (running) return;

      running = true;
      const startedAt = new Date().toISOString();

      await saveRecordingSession({
        sessionId,
        mimeType,
        preferredMimeType: preferredMimeType || mimeType || null,
        segmentDuration,
        capture,
        outputFormat: output?.format || 'mp4',
        fileNamePattern: output?.fileNamePattern || null,
        shareToCloud: output?.shareToCloud !== false,
        ffmpeg,
        videoBitsPerSecond,
        audioBitsPerSecond,
        state: 'recording',
        startedAt,
        updatedAt: startedAt
      });

      onStateChange?.('recording');
      syncPendingRecordings({ onProgress: onSyncProgress }).catch(() => {});
      startSegment();
    },

    async stop() {
      if (!running && !currentRecorder) return;

      running = false;
      clearTimeout(segmentTimer);

      if (currentRecorder?.state === 'recording') {
        currentRecorder.stop();
      }

      await segmentFinished;

      const stoppedAt = new Date().toISOString();
      await updateRecordingSession(sessionId, {
        state: 'stopping',
        expectedChunks: chunkIndex,
        stoppedAt
      });

      onStateChange?.('syncing');
      await syncPendingRecordings({ onProgress: onSyncProgress }).catch(() => {});
      onStateChange?.('stopped');
    }
  };
}
